<template>
  <div class="home">
    <!-- Hero -->
    <section class="hero">
      <img :src="bgHero" alt="" class="hero-bg-art" aria-hidden="true" />
      <ParticleField variant="dark" :count="52" />
      <div class="container hero-layout">
        <div class="hero-text">
          <p class="overline reveal">MTY Tools · Official product hub</p>
          <h1 class="reveal">Windows productivity apps<br />Fast, stable, local-first</h1>
          <p class="hero-desc reveal">
            A matrix of focused desktop utilities for real work.
            No ads, no junk plugins — built for Windows 8 / 10 / 11.
          </p>
          <div class="hero-actions reveal">
            <a href="#products" class="btn btn-white">Browse products</a>
            <a href="#about" class="btn btn-outline-light">About us</a>
          </div>
        </div>
        <div class="hero-gallery reveal" style="transition-delay:80ms">
          <img
            v-for="(shot, i) in galleryShots"
            :key="shot.alt"
            :src="shot.src"
            :alt="shot.alt"
            class="app-shot gallery-shot"
            :class="`gallery-shot--${i}`"
            loading="lazy"
          />
          <img
            :src="gifmakerHeroShot"
            alt="GifMaker"
            class="app-shot gallery-shot gallery-shot--overlay"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- Products -->
    <section class="showcase" id="products">
      <img :src="bgSection" alt="" class="showcase-bg-art" aria-hidden="true" />
      <img :src="bgShowcaseOrbs" alt="" class="showcase-orbs-art" aria-hidden="true" />
      <ParticleField variant="light" :count="28" />
      <div class="container">
        <header class="showcase-header reveal">
          <p class="overline">Products</p>
          <h2>Product matrix</h2>
          <p>Each app installs independently and focuses on one job.</p>
        </header>
      </div>

      <article
        v-for="(p, i) in products"
        :key="p.title"
        class="showcase-item reveal"
        :class="{ 'showcase-item--alt': i % 2 === 1 }"
      >
        <div class="container showcase-row">
          <div class="showcase-copy">
            <span class="showcase-index">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="showcase-title-row">
              <img :src="p.icon" :alt="p.title" class="showcase-icon" />
              <div>
                <h3>{{ p.title }}</h3>
                <p class="showcase-tag">{{ p.tagline }}</p>
              </div>
            </div>
            <p class="showcase-desc">{{ p.description }}</p>
            <ul class="showcase-features">
              <li v-for="f in p.features" :key="f">{{ f }}</li>
            </ul>
            <div class="showcase-actions">
              <a :href="p.downloadLink" :download="p.downloadFileName" class="btn-download">
                <img src="@/assets/videocvt/Windows.png" alt="" class="btn-os-icon" />
                Free download
              </a>
              <router-link :to="p.route" class="link-arrow">Details</router-link>
            </div>
          </div>
          <div class="showcase-preview shot-glow">
            <img :src="p.preview" :alt="`${p.title} preview`" class="app-shot" loading="lazy" />
          </div>
        </div>
      </article>
    </section>

    <!-- Standards -->
    <section class="standards">
      <img :src="bgStandards" alt="" class="standards-bg-art" aria-hidden="true" />
      <ParticleField variant="dark" :count="36" />
      <div class="container">
        <header class="standards-header reveal">
          <p class="overline">Standards</p>
          <h2>Our baseline standards</h2>
        </header>
        <div class="standards-grid reveal">
          <div v-for="s in standards" :key="s.title" class="standard">
            <span class="standard-num">{{ s.num }}</span>
            <h4>{{ s.title }}</h4>
            <p>{{ s.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About -->
    <section class="about" id="about">
      <img :src="bgAbout" alt="" class="about-bg-art" aria-hidden="true" />
      <SectionBackdrop :particles="true" variant="light" :count="18" />
      <div class="container about-layout reveal">
        <div class="about-copy">
          <p class="overline">About</p>
          <h2>MTY Tools</h2>
          <p>
            We build focused Windows desktop utilities — local-first,
            long-term maintained, and free of unnecessary clutter.
          </p>
          <p class="about-contact">
            Contact: <a href="mailto:support@mtytools.com">support@mtytools.com</a>
          </p>
        </div>
        <div class="about-stats">
          <div v-for="m in metrics" :key="m.label" class="about-stat">
            <strong>{{ m.value }}</strong>
            <span>{{ m.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta">
      <img :src="bgCta" alt="" class="cta-bg-art" aria-hidden="true" />
      <ParticleField variant="light" :count="22" />
      <div class="container cta-inner reveal">
        <h2>Pick the tool you need and get started</h2>
        <a href="#products" class="btn btn-dark">Browse &amp; download</a>
      </div>
    </section>
  </div>
</template>

<script>
import ParticleField from '@/components/ParticleField.vue'
import SectionBackdrop from '@/components/SectionBackdrop.vue'

export default {
  name: 'Home',
  components: { ParticleField, SectionBackdrop },
  data() {
    return {
      observer: null,
      bgHero: require('@/assets/illustrations/bg-hero-accent.svg'),
      bgSection: require('@/assets/illustrations/bg-section-flow.svg'),
      bgShowcaseOrbs: require('@/assets/illustrations/bg-showcase-orbs.svg'),
      bgStandards: require('@/assets/illustrations/bg-standards-glow.svg'),
      bgAbout: require('@/assets/illustrations/bg-about-rings.svg'),
      bgCta: require('@/assets/illustrations/bg-cta-soft.svg'),
      galleryShots: [
        { src: require('@/assets/fatestar/首页.png'), alt: 'FateStar' },
        { src: require('@/assets/longshot/首页.png'), alt: 'LongShot' },
        { src: require('@/assets/Everyfile/详细视图.png'), alt: 'EveryFile' },
        { src: require('@/assets/mtyzip/首页.png'), alt: 'MtyZip' },
        { src: require('@/assets/videocvt/mainpage.png'), alt: 'VideoCvt' },
        { src: require('@/assets/deskclock/时钟中心.png'), alt: 'DeskClock' }
      ],
      gifmakerHeroShot: require('@/assets/gifmaker/首页.png'),
      metrics: [
        { value: '7', label: 'Core apps' },
        { value: '100%', label: 'Runs locally' },
        { value: '0', label: 'Ads / junk plugins' },
        { value: 'Free', label: 'To get started' }
      ],
      standards: [
        { num: '01', title: 'Local-first', text: 'Core features run on your PC. We do not upload your files to the cloud.' },
        { num: '02', title: 'Reliable', text: 'Designed for daily use with careful Windows compatibility.' },
        { num: '03', title: 'Focused UI', text: 'Clear hierarchy, less clutter — finish the job faster.' },
        { num: '04', title: 'Maintained', text: 'We keep shipping updates based on real user feedback.' }
      ],
      products: [
        {
          title: 'GifMaker',
          shortName: 'GifMaker',
          tagline: 'Video to GIF · screen record · editor',
          icon: require('@/assets/gifmaker/logo_64x64.png'),
          preview: require('@/assets/gifmaker/首页.png'),
          description: 'Convert video or screen recordings to GIF locally — trim the timeline, control file size, and finish with the GIF toolbox. Nothing is uploaded.',
          features: ['Video / screen capture to GIF with quality presets', 'Timeline trim & batch convert', 'Compress, reverse, multi-image GIF & format convert'],
          route: '/product/gifmaker',
          downloadLink: '/ftp/packages/gifmaker/GifMakerSetup.exe',
          downloadFileName: 'GifMakerSetup.exe'
        },
        {
          title: 'DeskClock',
          shortName: 'DeskClock',
          tagline: 'Flip clock · desktop widget · screensaver',
          icon: require('@/assets/deskclock/deskclock_64x64.png'),
          preview: require('@/assets/deskclock/时钟中心.png'),
          description: 'Transparent flip and illustration clocks for your desktop — click-through widgets, calendar display, and full-screen screensaver mode.',
          features: ['Multiple themes & flip animations', 'Desktop widget with click-through', 'Full-screen screensaver & calendar'],
          route: '/product/deskclock',
          downloadLink: '/ftp/packages/deskclock/DeskClockSetup.exe',
          downloadFileName: 'DeskClockSetup.exe'
        },
        {
          title: 'VideoCvt',
          shortName: 'VideoCvt',
          tagline: 'Format convert · batch · HD output',
          icon: require('@/assets/videocvt/videoconvert_64x64.png'),
          preview: require('@/assets/videocvt/mainpage.png'),
          description: 'Convert mainstream video and audio formats with merge, split, watermark, and compress tools for creators and everyday office work.',
          features: ['Video / audio multi-format convert', 'Batch jobs with tunable settings', 'Merge, split, watermark, video to GIF'],
          route: '/product/video-converter',
          downloadLink: '/ftp/packages/videocvt/VideoCvtSetup.exe',
          downloadFileName: 'VideoCvtSetup.exe'
        },
        {
          title: 'MtyZip',
          shortName: 'MtyZip',
          tagline: 'ZIP / 7Z / RAR · encrypted archives',
          icon: require('@/assets/mtyzip/zip_logo64.png'),
          preview: require('@/assets/mtyzip/首页.png'),
          description: 'Fast, reliable compress and extract — multi-format archives, password protection, archive preview, and special compress modes.',
          features: ['ZIP / 7Z / RAR and more', 'Archive preview & password protect', 'PDF / image / video compression'],
          route: '/product/compress',
          downloadLink: '/ftp/packages/mtyzip/MtyZipSetup.exe',
          downloadFileName: 'MtyZipSetup.exe'
        },
        {
          title: 'EveryFile',
          shortName: 'EveryFile',
          tagline: 'Instant · full-disk file search',
          icon: require('@/assets/Everyfile/everyfile_64x64.png'),
          preview: require('@/assets/Everyfile/详细视图.png'),
          description: 'High-performance local indexing for keyword search, filters, multi-view browsing, and favorites.',
          features: ['Near-instant full-disk search', 'List / icon views', 'Favorites & smart filters'],
          route: '/product/everyfile',
          downloadLink: '/ftp/packages/everyfile/EveryfileSetup.exe',
          downloadFileName: 'EveryfileSetup.exe'
        },
        {
          title: 'LongShot',
          shortName: 'LongShot',
          tagline: 'Scrolling capture · smart stitch · OCR',
          icon: require('@/assets/longshot/logo_64x64.png'),
          preview: require('@/assets/longshot/首页.png'),
          description: 'Auto-scroll to capture long pages, stitch seamlessly, then preview, edit, and extract text with built-in OCR.',
          features: ['Auto scrolling long-page capture', 'Smart seamless stitching', 'OCR & capture history'],
          route: '/product/longshot',
          downloadLink: '/ftp/packages/longshot/LongShotSetup.exe',
          downloadFileName: 'LongShotSetup.exe'
        },
        {
          title: 'FateStar',
          shortName: 'FateStar',
          tagline: 'Almanac · BaZi · astrology tools',
          icon: require('@/assets/fatestar/fatestar_64x64.png'),
          preview: require('@/assets/fatestar/首页.png'),
          description: 'Traditional calendar and culture tools — daily almanac, BaZi charts, astrology insights, and baby naming helpers.',
          features: ['Daily almanac & auspicious dates', 'BaZi chart & luck cycles', 'Astrology & compatibility'],
          route: '/product/fatestar',
          downloadLink: '/ftp/packages/fatestar/FateStarSetup.exe',
          downloadFileName: 'FateStarSetup.exe'
        }
      ]
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            this.observer.unobserve(e.target)
          }
        })
      },
      { threshold: 0.08 }
    )
    this.$el.querySelectorAll('.reveal').forEach(el => this.observer.observe(el))
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  }
}
</script>

<style scoped>
.home { padding-top: var(--header-h); }

/* ── Hero ── */
.hero {
  position: relative;
  background: var(--surface-dark);
  color: #fff;
  padding: 80px 0 0;
  overflow: hidden;
}

.hero-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.9;
  z-index: 0;
}

.hero-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 48px;
  align-items: center;
  padding-bottom: 64px;
}

.hero-text h1 {
  font-size: clamp(2.25rem, 4.5vw, 3.25rem);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin: 16px 0 20px;
}

.hero-text .overline { color: rgba(255, 255, 255, 0.45); }

.hero-desc {
  font-size: 16px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.6);
  max-width: 440px;
  margin-bottom: 32px;
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-gallery {
  position: relative;
  height: 360px;
  min-height: 300px;
}

.hero-gallery .gallery-shot {
  position: absolute;
  width: 78%;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  transition: transform 0.35s var(--motion-ease);
}

/* Fan-stacked gallery shots */
.hero-gallery .gallery-shot--0 {
  left: 0;
  top: 56px;
  z-index: 1;
  transform: rotate(-2deg);
}

.hero-gallery .gallery-shot--1 {
  left: 0;
  top: 40px;
  z-index: 2;
  transform: rotate(-1.5deg);
}

.hero-gallery .gallery-shot--2 {
  right: 0;
  top: 48px;
  z-index: 3;
  transform: rotate(1.8deg);
}

.hero-gallery .gallery-shot--3 {
  right: 0;
  top: 30px;
  z-index: 4;
  transform: rotate(1.3deg);
}

.hero-gallery .gallery-shot--4 {
  left: 6%;
  top: 14px;
  z-index: 5;
  transform: rotate(-0.5deg);
}

.hero-gallery .gallery-shot--5 {
  left: 11%;
  top: 0;
  z-index: 6;
}

/* Top overlay: GifMaker hero shot */
.hero-gallery .gallery-shot--overlay {
  left: 11%;
  top: 0;
  z-index: 7;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.42);
}

.hero-gallery:hover .gallery-shot--0 { transform: rotate(-2deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--1 { transform: rotate(-1.5deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--2 { transform: rotate(1.8deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--3 { transform: rotate(1.3deg) translateY(-4px); }
.hero-gallery:hover .gallery-shot--4 { transform: rotate(-0.5deg) translateY(-5px); }
.hero-gallery:hover .gallery-shot--5 { transform: translateY(-6px); }
.hero-gallery:hover .gallery-shot--overlay { transform: translateY(-8px); }

/* ── Showcase ── */
.showcase {
  position: relative;
  padding: 100px 0 40px;
  scroll-margin-top: var(--header-h);
  overflow: hidden;
}

.showcase-bg-art {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120%;
  max-width: 1400px;
  opacity: 0.55;
  pointer-events: none;
  z-index: 0;
}

.showcase-orbs-art {
  position: absolute;
  left: -60px;
  top: 10%;
  width: 280px;
  opacity: 0.7;
  pointer-events: none;
  z-index: 0;
}

.showcase .container,
.showcase-item {
  position: relative;
  z-index: 2;
}

.showcase-header {
  position: relative;
  max-width: 520px;
  margin-bottom: 64px;
}

.showcase-header h2 {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 12px;
}

.showcase-header p {
  color: var(--ink-muted);
  font-size: 16px;
  line-height: 1.7;
}

.showcase-item {
  position: relative;
  padding: 80px 0;
  border-top: 1px solid var(--line);
}

.showcase-item--alt {
  background: linear-gradient(180deg, var(--surface-subtle) 0%, rgba(250, 250, 250, 0.6) 100%);
}

.showcase-row {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
}

.showcase-item--alt .showcase-row {
  direction: rtl;
}

.showcase-item--alt .showcase-row > * {
  direction: ltr;
}

.showcase-index {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-faint);
  letter-spacing: 0.05em;
  margin-bottom: 20px;
}

.showcase-title-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.showcase-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
}

.showcase-copy h3 {
  font-size: 1.375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.showcase-tag {
  font-size: 13px;
  color: var(--ink-muted);
  margin-top: 4px;
}

.showcase-desc {
  font-size: 15px;
  color: var(--ink-secondary);
  line-height: 1.75;
  margin-bottom: 20px;
}

.showcase-features {
  list-style: none;
  margin-bottom: 28px;
}

.showcase-features li {
  position: relative;
  padding-left: 16px;
  font-size: 14px;
  color: var(--ink-muted);
  margin-bottom: 8px;
}

.showcase-features li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--accent-brand);
}

.showcase-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.link-arrow {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-secondary);
  transition: color var(--motion-fast);
}

.link-arrow:hover { color: var(--accent-brand); }

.link-arrow::after {
  content: ' →';
}

/* ── Standards ── */
.standards {
  position: relative;
  padding: 100px 0;
  background: var(--surface-dark);
  color: #fff;
  overflow: hidden;
}

.standards-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.85;
  z-index: 0;
}

.standards .container {
  position: relative;
  z-index: 2;
}

.standards-header {
  margin-bottom: 48px;
}

.standards-header .overline { color: rgba(255, 255, 255, 0.4); }

.standards-header h2 {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-top: 8px;
}

.standards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.standard {
  padding: 32px 28px;
  background: var(--surface-dark-elevated);
  transition: background var(--motion-fast);
}

.standard:hover {
  background: rgba(24, 24, 27, 0.95);
}

.standard-num {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent-brand);
  letter-spacing: 0.06em;
  margin-bottom: 16px;
}

.standard h4 {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 8px;
}

.standard p {
  font-size: 13px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.5);
}

/* ── About ── */
.about {
  position: relative;
  padding: 100px 0;
  border-top: 1px solid var(--line);
  overflow: hidden;
}

.about-bg-art {
  position: absolute;
  right: -80px;
  top: 50%;
  transform: translateY(-50%);
  width: 480px;
  opacity: 0.45;
  pointer-events: none;
  z-index: 0;
}

.about-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 64px;
  align-items: start;
}

.about-copy h2 {
  font-size: clamp(1.5rem, 3vw, 1.875rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 16px;
}

.about-copy p {
  color: var(--ink-secondary);
  line-height: 1.75;
  font-size: 15px;
}

.about-contact {
  margin-top: 20px;
  font-size: 14px;
}

.about-contact a {
  color: var(--accent-brand);
  font-weight: 500;
}

.about-contact a:hover { text-decoration: underline; }

.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.about-stat {
  padding: 28px 24px;
  background: var(--surface);
  text-align: center;
  transition: transform var(--motion-fast) var(--motion-ease), box-shadow var(--motion-fast);
}

.about-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(4, 120, 87, 0.08);
}

.about-stat strong {
  display: block;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  margin-bottom: 4px;
}

.about-stat span {
  font-size: 13px;
  color: var(--ink-muted);
}

/* ── CTA ── */
.cta {
  position: relative;
  padding: 80px 0 100px;
  background: var(--surface-subtle);
  border-top: 1px solid var(--line);
  overflow: hidden;
}

.cta-bg-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.9;
  z-index: 0;
}

.cta-inner {
  position: relative;
  z-index: 2;
  text-align: center;
}

.cta-inner h2 {
  font-size: clamp(1.375rem, 2.5vw, 1.75rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-bottom: 28px;
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .hero-layout,
  .showcase-row,
  .about-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-gallery {
    height: auto;
    min-height: 0;
    max-width: 520px;
    margin: 0 auto;
    padding: 8px 0;
  }

  .hero-gallery .gallery-shot:not(.gallery-shot--overlay):not(:last-of-type) {
    display: none;
  }

  .hero-gallery .gallery-shot--overlay,
  .hero-gallery .gallery-shot:last-of-type {
    position: relative;
    width: 100%;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    transform: none !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  }

  .showcase-orbs-art { display: none; }

  .showcase-item--alt .showcase-row { direction: ltr; }

  .standards-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 640px) {
  .hero { padding-top: 48px; }
  .standards-grid { grid-template-columns: 1fr; }
  .about-stats { grid-template-columns: 1fr; }
}
</style>
